// store/slices/sessionSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { sessionService } from "../../services/session.service";
import { creditService } from "../../services/credit.service";
import { Timestamp } from "firebase/firestore";

export const fetchAvailableSlots = createAsyncThunk(
	"sessions/fetchAvailableSlots",
	async (date) => {
		console.log("Fetching slots for date:", date);
		const serializedDate = date.toISOString();
		const slots = await sessionService.getAvailableSlots(
			new Date(serializedDate)
		);
		console.log("Received slots:", slots);
		return slots;
	}
);

export const requestSession = createAsyncThunk(
	"sessions/requestSession",
	async ({ date, timeSlot, duration, notes }, { getState }) => {
		const { auth } = getState();

		// First validate if user has enough credits
		const validation = await creditService.validateBooking(
			auth.user.uid,
			duration
		);
		if (!validation.valid) {
			throw new Error(validation.message);
		}

		const [hours, minutes] = timeSlot.split(":");
		const sessionDate = new Date(date);
		sessionDate.setHours(parseInt(hours), parseInt(minutes), 0);

		// If validation passes, create session request
		const sessionData = {
			date: sessionDate,
			timeSlot,
			duration,
			notes,
			type: "regular",
			status: "pending",
		};

		const sessionId = await sessionService.requestSession(
			auth.user.uid,
			sessionData
		);
		return { id: sessionId, ...sessionData };
	}
);

export const cancelSession = createAsyncThunk(
	"sessions/cancelSession",
	async (sessionId) => {
		await sessionService.cancelSession(sessionId);
		return sessionId;
	}
);

export const rescheduleSession = createAsyncThunk(
	"sessions/rescheduleSession",
	async ({ sessionId, newDate, newTimeSlot }) => {
		await sessionService.rescheduleSession(sessionId, newDate, newTimeSlot);
		return { sessionId, newDate, newTimeSlot };
	}
);

export const fetchUserSessions = createAsyncThunk(
	"sessions/fetchUserSessions",
	async (userId) => {
		const sessions = await sessionService.getUserSessions(userId);
		return sessions;
	},
	{
		condition: (_, { getState }) => {
			const { sessions } = getState();
			// Only fetch if we haven't fetched before or explicitly want to refresh
			return !sessions.dataFetched;
		},
	}
);

const sessionSlice = createSlice({
	name: "sessions",
	initialState: {
		userSessions: [], // User's booked sessions
		availableSlots: [], // Available time slots for selected date
		selectedDate: null, // Currently selected date for booking
		loading: false, // Loading state
		error: null, // Error state
		dataFetched: false,
		activeSession: null, // Currently viewed/edited session
	},
	reducers: {
		setSelectedDate: (state, action) => {
			state.selectedDate = action.payload;
		},
		clearActiveSession: (state) => {
			state.activeSession = null;
		},
		setError: (state, action) => {
			state.error = action.payload;
		},
		clearError: (state) => {
			state.error = null;
		},
		resetSessionState: (state) => {
			state.dataFetched = false;
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch Available Slots
			.addCase(fetchAvailableSlots.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchAvailableSlots.fulfilled, (state, action) => {
				state.loading = false;
				state.availableSlots = action.payload;
			})
			.addCase(fetchAvailableSlots.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})
			// Request Session
			.addCase(requestSession.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(requestSession.fulfilled, (state, action) => {
				state.loading = false;
				state.userSessions.unshift(action.payload);
			})
			.addCase(requestSession.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})
			// Cancel Session
			.addCase(cancelSession.fulfilled, (state, action) => {
				state.userSessions = state.userSessions.map((session) =>
					session.id === action.payload
						? { ...session, status: "cancelled" }
						: session
				);
			})
			// Reschedule Session
			.addCase(rescheduleSession.fulfilled, (state, action) => {
				state.userSessions = state.userSessions.map((session) =>
					session.id === action.payload.sessionId
						? {
								...session,
								date: action.payload.newDate,
								timeSlot: action.payload.newTimeSlot,
								status: "rescheduled",
						  }
						: session
				);
			})
			// Fetch User Sessions
			.addCase(fetchUserSessions.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchUserSessions.fulfilled, (state, action) => {
				state.loading = false;
				state.userSessions = action.payload || [];
				state.dataFetched = true;
				state.error = null;
			})
			.addCase(fetchUserSessions.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
				state.dataFetched = true;
			});
	},
});

export const {
	setSelectedDate,
	clearActiveSession,
	setError,
	clearError,
	resetSessionState,
} = sessionSlice.actions;

// Selectors
export const selectUserSessions = (state) => state.sessions.userSessions;
export const selectAvailableSlots = (state) => state.sessions.availableSlots;
export const selectSessionsLoading = (state) => state.sessions.loading;
export const selectSessionError = (state) => state.sessions.error;
export const selectSelectedDate = (state) => state.sessions.selectedDate;
export const selectActiveSession = (state) => state.sessions.activeSession;

export default sessionSlice.reducer;
